import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useLiveSocket, type LiveSocketStatus } from './useLiveSocket';

/**
 * ONE live socket for the whole authenticated app (slice 56g).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS.
 * ─────────────────────────────────────────────────────────────────────────────
 * Before this slice `useLiveSocket()` was called INSIDE `LiveMap` and inside
 * `VehicleDetail` — each mount opened its own WebSocket, minted its own ticket,
 * and tore the socket down on navigation away. The Overview map was NOT live at
 * all (it read the 60 s poll). Making the Overview live by adding a fourth
 * `useLiveSocket()` call would have meant a socket that closes and re-opens every
 * time the user moves between Overview and the Live map — churn on exactly the
 * metered Mogadishu connection the whole live path is built to spare.
 *
 * So the hook is hoisted to ONE provider mounted once, ABOVE the routes and
 * INSIDE `RequireAuth` (see RequireAuth.tsx). One socket for the session; it does
 * not tear down when the route changes, because the provider does not unmount
 * when the `<Outlet/>` under it swaps. Every screen reads the status through the
 * context instead of opening its own connection.
 *
 * 🔴 MOUNTED INSIDE AUTH, DELIBERATELY. The socket mints a ticket via an
 * authenticated `POST /api/live/ticket`; a logged-out visitor or the public share
 * page (`/t/:token`, which lives OUTSIDE RequireAuth) must never open it. Because
 * the provider is a child of the authenticated branch of RequireAuth, an
 * unauthenticated render never reaches it and no ticket is ever minted for a
 * session that has none. Staff (impersonation gate) render `StaffPanel` before
 * this provider, so they never open a customer socket either.
 *
 * The socket's full lifecycle — ticket mint, bounded backoff, visibilitychange
 * zero-bytes pause/resume, AuthError/403 terminal stop — is unchanged and still
 * lives in `useLiveSocket`; this file only decides WHERE it is called and how the
 * status reaches consumers.
 */

const LiveSocketContext = createContext<{ status: LiveSocketStatus }>({ status: 'idle' });

export function LiveSocketProvider({ children }: { children: ReactNode }) {
  const socket = useLiveSocket();
  // `useLiveSocket` returns a fresh `{ status }` object each render; memoise on
  // the primitive so the context value's identity changes only when the status
  // actually changes, not on every re-render of whatever sits above the provider.
  const value = useMemo(() => ({ status: socket.status }), [socket.status]);
  return <LiveSocketContext.Provider value={value}>{children}</LiveSocketContext.Provider>;
}

/**
 * Read the app-wide live-socket status. Consumers that are NOT wrapped in a
 * provider (a component rendered outside the authenticated tree, or a test that
 * mounts a screen in isolation) get the inert default `{ status: 'idle' }`, which
 * makes `useLiveVehicles` fall back to its 60 s poll — the correct degradation,
 * never a crash.
 */
export function useLiveSocketStatus(): { status: LiveSocketStatus } {
  return useContext(LiveSocketContext);
}
